<template>
  <div class="space-y-6">
    <div class="flex flex-col gap-6">
      <div class="flex flex-wrap items-center justify-between gap-4">
        <div class="flex items-center gap-3">
          <h3 class="text-xl font-bold text-slate-800">Users ({{ users.length }})</h3>
        </div>
        <div class="flex items-center gap-2">
          <button
            v-for="opt in statusFilterOptions"
            :key="opt.value"
            class="flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium transition-colors"
            :class="statusFilter === opt.value
              ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
              : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:text-slate-700'"
            @click="statusFilter = opt.value"
          >
            <span
              v-if="opt.value !== 'all'"
              class="h-2 w-2 rounded-full flex-shrink-0"
              :style="{ backgroundColor: opt.value === 'active' ? '#22c55e' : '#ef4444' }"
            ></span>
            <span>{{ opt.label }}</span>
            <span class="ml-1 rounded-full px-1 py-0 text-xs font-semibold leading-5"
              :class="statusFilter === opt.value ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-600'"
            >{{ opt.count }}</span>
          </button>
        </div>
      </div>
      <div class="flex items-center justify-between gap-3 w-full">
        <div class="flex gap-4">
          <Search :value="searchTerm" placeholder="Search by name and email..." @change="searchTerm = $event" />
          <Dropdown
            :options="statusFilterOptions"
            :value="statusFilter"
            :getLabel="(option) => option.label"
            :getValue="(option) => option.value"
            placeholder="Filter by status"
            variant="simple"
            @update:value="onStatusFilterChange"
          />
          <CustomFilter
            v-model="filtersModel"
            :filter-definitions="filterDefinitions"
            :is-loading="loading"
            :error="error"
            @apply="handleFilterApply"
          />
        </div>
             <SortDropdown
          label="Users"
          :options="sortOptions"
          :model-value="sortBy"
          placeholder="Date Added"
          @update:model-value="onSortChange"
        />
      </div>
      <div v-if="loading" class="text-center py-8">
        <div class="inline-flex items-center gap-2 text-slate-500">
          <svg class="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
            </path>
          </svg>
          Loading users...
        </div>
      </div>
      <div v-else-if="error" class="text-center py-8">
        <div class="text-red-600">
          {{ error }}
        </div>
      </div>
      <div v-else-if="filteredUsers.length > 0" class="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <article v-for="(user, index) in filteredUsers" :key="`${user.id}-${index}`" :class="cardClass()">
          <span class="absolute inset-x-0 top-0 h-1 rounded-t" :class="user.status === 'active' ? 'bg-green-700' : 'bg-red-700'"></span>
          <div class="flex items-start justify-between pt-2">
            <div class="space-y-1 text-xs text-slate-500">
              <div class="flex items-center gap-2">
                <span :class="statusDotClass(user.status)"></span>
                <span :class="[
                  'font-medium text-sm capitalize',
                  statusTextClass(user.status)
                ]">{{ user.status }}</span>
              </div>
              <p class="text-sm font-semibold text-black">
                {{ user.name }}
              </p>
              <p class="text-slate-700 text-xs">
                <Icon icon-name="MailIcon" /> {{ user.email }}
              </p>
              <p class="text-slate-700 text-xs">
                <Icon icon-name="CallIcon" /> {{ user.phone }}
              </p>
            </div>
            <span v-if="user.role"
              class="rounded-lg border border-blue-500 bg-blue-100 px-3 py-1 text-xs font-medium text-blue-800">
              {{ user.role }}
            </span>
          </div>

          <dl class="grid grid-cols-1 gap-2 text-xs text-slate-500">
            <div class="flex gap-1">
              <dt class="w-16 shrink-0 font-medium text-slate-600 text-xs">
                Position
              </dt>
              <dd class="text-slate-800 text-xs">: {{ user.position }}</dd>
            </div>
            <div class="flex gap-1">
              <dt class="w-16 shrink-0 font-medium text-slate-600 text-xs">
                Office
              </dt>
              <dd class="text-slate-800 text-xs">: <span class="text-blue-600 text-sm font-semibold">{{ user.office
                  }},</span> {{ user.location }}</dd>
            </div>
            <div class="flex gap-1">
              <dt class="w-16 shrink-0 font-medium text-slate-600 text-xs">
                Invited By
              </dt>
              <dd class="text-slate-800 text-xs">: {{ user.invitedBy }}</dd>
            </div>
            <div class="flex gap-1">
              <dt class="w-20 shrink-0 font-medium text-slate-600 text-xs">
                Joined Date
              </dt>
              <dd class="text-slate-800 text-xs">: {{ user.joinedDate }}</dd>
            </div>
          </dl>
        </article>
      </div>
      <EmptyState v-else />
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import Search from "../core/Search.vue";
import Icon from "../core/Icon.vue";
import EmptyState from "../Forms/EmptyState.vue";
import Dropdown from "../core/Dropdown.vue";
import CustomFilter from "../core/Filter.vue";
import SortDropdown from "../core/SortDropdown.vue";

export default {
  name: "UsersTab",

  components: {
    Search,
    Icon,
    EmptyState,
    Dropdown,
    CustomFilter,
    SortDropdown
  },

  props: {
    tenantId: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      searchTerm: "",
      statusFilter: "all",
      showActiveOnly: false,
      users: [],
      loading: false,
      error: null,
      filtersModel: [],
      filterPayload: {},
      sortBy: "lastModifiedDesc"
    };
  },
  computed: {
    ...mapState("TenantAnalyticsStore", {
      storedTenantUsers: (state) => state.tenantUsers,
    }),
    activeCount() {
      return this.users.filter(u => u.status === "active").length;
    },
    inactiveCount() {
      return this.users.filter(u => u.status === "inactive").length;
    },
    statusFilterOptions() {
      return [
        { label: "All",      value: "all",      count: this.users.length },
        { label: "Active",   value: "active",   count: this.activeCount },
        { label: "Inactive", value: "inactive", count: this.inactiveCount },
      ];
    },
    statusFilterFieldOptions() {
      return this.statusFilterOptions.filter((option) => option.value !== "all").map((option) => ({
        label: option.label.replace(" Users", ""),
        value: option.value
      }));
    },
    roleOptions() {
      return [
        { label: "Super Admin", value: 1 },
        { label: "Admin", value: 2 },
        { label: "Manager", value: 3 },
        { label: "Accountant", value: 4 },
        { label: "Counsellor", value: 5 },
        { label: "Operator", value: 6 }
      ];
    },
    filterDefinitions() {
      const textOperators = [
        { label: "Contains", value: "contains" },
        { label: "Is", value: "is" }
      ];

      return [
        {
          key: "role_id",
          label: "Role",
          valueType: "select",
          operators: [{ label: "Is", value: "is" }],
          options: this.roleOptions
        },
        {
          key: "position",
          label: "Position",
          valueType: "text",
          operators: textOperators
        },
        {
          key: "invited_by",
          label: "Invited By",
          valueType: "text",
          operators: textOperators
        },
        {
          key: "join_date",
          label: "Joined Date",
          valueType: "date",
          operators: [
            { label: "Is", value: "is" },
            { label: "Before", value: "before" },
            { label: "After", value: "after" }
          ]
        },
        {
          key: "office",
          label: "Office",
          valueType: "text",
          operators: textOperators
        },
        {
          key: "status",
          label: "Status",
          valueType: "select",
          operators: [{ label: "Is", value: "is" }],
          options: this.statusFilterFieldOptions
        }
      ];
    },
    sortOptions() {
      return [
        { label: "Last Modified Date", value: "lastModifiedDesc" },
        { label: "User’s First Name A-Z", value: "firstNameAsc" },
        { label: "User’s First Name Z-A", value: "firstNameDesc" }
      ];
    },
    filteredUsers() {
      let filtered = this.users.filter(user => {
        const matchesSearch = this.searchTerm
          ? [user.name, user.email].some(field =>
            (field || "").toLowerCase().includes(this.searchTerm.toLowerCase())
          )
          : true;
        
        let matchesStatus = true;
        if (this.statusFilter === "active") {
          matchesStatus = user.status === "active";
        } else if (this.statusFilter === "inactive") {
          matchesStatus = user.status === "inactive";
        }
        
        if (this.showActiveOnly) {
          matchesStatus = matchesStatus && user.status === "active";
        }
        
        const matchesFilters = this.matchesUserFilters(user);

        return matchesSearch && matchesStatus && matchesFilters;
      });

      // Sort the filtered results
      return filtered.sort((a, b) => {
        if (this.sortBy === "lastModifiedDesc") {
          const aDate = new Date(a.updatedAtISO || a.joinedDateISO || 0);
          const bDate = new Date(b.updatedAtISO || b.joinedDateISO || 0);
          return bDate.getTime() - aDate.getTime();
        }

        if (this.sortBy === "firstNameAsc" || this.sortBy === "firstNameDesc") {
          const direction = this.sortBy === "firstNameAsc" ? 1 : -1;
          const aName = (a.firstName || a.name || "").toLowerCase();
          const bName = (b.firstName || b.name || "").toLowerCase();
          return direction * aName.localeCompare(bName);
        }

        return 0;
      });
    }
  },
  methods: {
    handleFilterApply(payload) {
      this.filterPayload = payload?.filters || {};
      this.fetchUsersWithFilters();
    },
    matchesUserFilters(user) {
      if (!this.filterPayload || Object.keys(this.filterPayload).length === 0) {
        return true;
      }

      return Object.entries(this.filterPayload).every(([fieldKey, operatorMap]) => {
        return Object.entries(operatorMap).every(([operator, value]) => {
          return this.evaluateFilter(fieldKey, operator, value, user);
        });
      });
    },
    resolveUserFieldValue(user, fieldKey) {
      const map = {
        role_id: user.roleId,
        position: user.position,
        invited_by: user.invitedBy,
        join_date: user.joinedDateISO,
        office: user.office,
        status: user.status,
      };

      return map[fieldKey];
    },
    evaluateFilter(fieldKey, operator, value, user) {
      const fieldValue = this.resolveUserFieldValue(user, fieldKey);

      if (fieldValue === undefined || fieldValue === null || fieldValue === "") {
        return false;
      }

      if (operator === "contains") {
        return String(fieldValue).toLowerCase().includes(String(value).toLowerCase());
      }

      if (operator === "is") {
        if (fieldKey === "join_date") {
          return this.compareDates(fieldValue, value) === 0;
        }
        return String(fieldValue).toLowerCase() === String(value).toLowerCase();
      }

      if (operator === "before") {
        return this.compareDates(fieldValue, value) < 0;
      }

      if (operator === "after") {
        return this.compareDates(fieldValue, value) > 0;
      }

      if (operator === "is_not") {
        if (fieldKey === "join_date") {
          return this.compareDates(fieldValue, value) !== 0;
        }
        return String(fieldValue).toLowerCase() !== String(value).toLowerCase();
      }

      return true;
    },
    compareDates(source, target) {
      const sourceDate = this.parseFilterDate(source);
      const targetDate = this.parseFilterDate(target);

      if (!sourceDate || !targetDate) {
        return 0;
      }

      const sourceTime = sourceDate.setHours(0, 0, 0, 0);
      const targetTime = targetDate.setHours(0, 0, 0, 0);

      if (sourceTime === targetTime) {
        return 0;
      }

      return sourceTime < targetTime ? -1 : 1;
    },
    parseFilterDate(value) {
      if (!value) {
        return null;
      }

      const date = new Date(String(value));
      if (Number.isNaN(date.getTime())) {
        return null;
      }

      return date;
    },
    async fetchUsers() {
      this.loading = true;
      this.error = null;
      try {
        await this.fetchUsersFromApi();
      } catch (error) {
        this.error = 'Failed to load users';
        this.users = [];
      } finally {
        this.loading = false;
      }
    },
    async fetchUsersFromApi(filters = null) {
      let users = this.storedTenantUsers;

      if (!filters && (!Array.isArray(users) || users.length === 0)) {
        users = await this.$store.dispatch(
          "TenantAnalyticsStore/fetchTenantUsers",
          { tenantId: this.tenantId }
        );
      }

      if (filters) {
        users = await this.$store.dispatch(
          "TenantAnalyticsStore/fetchTenantUsers",
          { tenantId: this.tenantId, filters }
        );
      }

      const userList = Array.isArray(users?.data) ? users.data : users;
      this.users = this.mapUsersData(userList || []);
    },
    async fetchUsersWithFilters() {
      const filters = this.buildApiFilterPayload();
      await this.fetchUsersFromApi(filters);
    },
    buildApiFilterPayload() {
      const payload = { tenant_id: this.tenantId };

      if (this.searchTerm) {
        payload.search_text = this.searchTerm;
      }

      if (this.statusFilter === "active") {
        payload.status = 1;
      } else if (this.statusFilter === "inactive") {
        payload.status = 0;
      }

      if (!this.filterPayload || !Object.keys(this.filterPayload).length) {
        return payload;
      }

      Object.entries(this.filterPayload).forEach(([fieldKey, operatorMap]) => {
        Object.entries(operatorMap).forEach(([operator, value]) => {
          if (!value && value !== 0) {
            return;
          }

          if (fieldKey === "join_date") {
            payload.join_date = value;
          } else if (fieldKey === "role_id") {
            payload.role_id = value;
            payload.role_operator = operator;
          } else if (fieldKey === "position") {
            payload.position = value;
            payload.position_operator = operator;
          } else if (fieldKey === "invited_by") {
            payload.invited_by = value;
            payload.invited_by_operator = operator;
          } else if (fieldKey === "office") {
            payload.office = value;
            payload.office_operator = operator;
          } else if (fieldKey === "status") {
            payload.status = value === "active" ? 1 : 0;
          }
        });
      });

      return payload;
    },
    mapUsersData(apiUsers) {
      const formatDate = (value) => {
        if (!value) {
          return "";
        }

        const date = new Date(value.replace(/ /g, "T"));
        if (Number.isNaN(date.getTime())) {
          return value;
        }
        return date.toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      };

      const firstDefined = (...values) => {
        for (const value of values) {
          if (value !== undefined && value !== null && value !== "") {
            return value;
          }
        }
        return null;
      };

      return apiUsers.map(user => {
        const fullName = `${user.first_name || ""} ${user.last_name || ""}`.trim();
        const statusValue = firstDefined(user.status, user.status_label, user.statusLabel, user.status_name);
        const status = ["active", "1", 1, true].includes(statusValue) ? "active" : "inactive";

        const phone = firstDefined(user.mobile, user.phone, user.contact_number, user.phone_number) || "-";

        const invitedBy = (() => {
          const firstName = firstDefined(user.added_by_first_name, user.invited_by_first_name, user.invitedByFirstName);
          const lastName = firstDefined(user.added_by_last_name, user.invited_by_last_name, user.invitedByLastName);
          const parts = [firstName, lastName].filter(Boolean);
          return parts.length ? parts.join(" ") : "-";
        })();

        const joinedDateRaw = firstDefined(user.created_at, user.joined_at, user.joinedAt, user.createdAt);
        const updatedDateRaw = firstDefined(user.updated_at, user.updatedAt, user.last_modified_at, user.lastModifiedAt);
        const joinedDate = formatDate(joinedDateRaw);
        const joinedDateISO = (() => {
          if (!joinedDateRaw) {
            return null;
          }
          const normalized = String(joinedDateRaw).replace(/ /g, "T");
          const parsed = new Date(normalized);
          if (Number.isNaN(parsed.getTime())) {
            return null;
          }
          return parsed.toISOString().split("T")[0];
        })();

        const secondaryBranches = user.secondary_branches || user.secondaryBranches || [];
        const location = Array.isArray(secondaryBranches)
          ? secondaryBranches.join(", ")
          : (secondaryBranches || "");

        return {
          id: user.id,
          status,
          name: fullName || user.email,
          firstName: user.first_name || user.firstName || "",
          email: user.email,
          phone,
          position: user.job_title || user.jobTitle || "",
          office: user.branch_name || user.branchName || "",
          location,
          invitedBy,
          joinedDate,
          joinedDateISO,
          updatedAtISO: (() => {
            if (!updatedDateRaw) {
              return null;
            }
            const normalized = String(updatedDateRaw).replace(/ /g, "T");
            const parsed = new Date(normalized);
            if (Number.isNaN(parsed.getTime())) {
              return null;
            }
            return parsed.toISOString();
          })(),
          role: user.role_name || user.roleName || "",
          roleId: user.role_id || user.roleId || null,
          isOwner: Number(user.is_owner) === 1,
        };
      });
    },
    cardClass() {
      const base =
        "relative flex h-full flex-col gap-4 rounded border-t-transparent bg-white px-6 pt-5 pb-4 shadow-sm";
      return [base];
    },
    statusDotClass(status) {
      return [
        "h-2 w-2 rounded-full",
        status === "active" ? "bg-green-700" : "bg-red-700"
      ];
    },
    statusTextClass(status) {
      return status === "active" ? "text-green-700" : "text-red-700";
    },
    onStatusFilterChange(newValue) {
      this.statusFilter = newValue.value;
    },
    onSortChange(newValue) {
      this.sortBy = newValue;
    }
  },
  async mounted() {
    await this.fetchUsers();
  }
};
</script>
